import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Upload } from 'lucide-react';
import { apiUrl } from '../../config/api';
import { AuthContext } from '../../context/AuthContext';
import { formatDateDMY } from '../../utils/dateUtils';
import { toCurrency } from '../../utils/adminMetrics';
import { TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from '../UI/tableSort';
import { FIELD_LABEL, fieldInputClass } from '../UI/DisplayField';
import { BankIcon } from '../UI/IconLibrary';
import { LoanSection } from '../Forms/LoanSections';
import { RecalculateCalculation } from '../CalculatorDecline';
import {
  ACCOUNT_TYPES, ACCOUNT_STATUSES, SOURCES, BUREAUS, OTHER_BUREAU, bureauChoice, blankAccount, toPayload, toForm, bureauTotals, reportAgeDays, fromClientReport,
} from './bureauView';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const cell = 'px-1.5 py-1';
const COLUMNS = [
  { label: 'Creditor' }, { label: 'Account type' }, { label: 'Instalment (N$)', right: true },
  { label: 'Balance (N$)', right: true }, { label: 'Status' }, { label: 'Report date' }, { label: 'Source' }, { label: '' },
];
const sourceLabel = (v) => (SOURCES.find((x) => x.value === v) || SOURCES[0]).label;

/**
 * The credit bureau report on a loan application (Microlending Act s 24(4)):
 * the PDF, the report's date, and its accounts typed into a grid. The
 * affordability check reads the accounts when the calculation is run again
 * (the button under the grid); the report date drives its 30-day rule.
 * Staff only. onChanged: after a save, an upload or a rerun (to reload the
 * loan's documents and calculator).
 */
const BureauReportSection = ({ loan, calculator = null, className = '', onChanged }) => {
  const { user } = useContext(AuthContext) || {};
  const perms = user?.permissions || [];
  const isStaff = perms.includes('*') || perms.includes('ViewLoans') || perms.includes('ManageLoans');
  const mayEdit = perms.includes('*') || perms.includes('EditLoan') || perms.includes('ManageLoans');
  const [data, setData] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  // "Other" chosen in the bureau dropdown: the name is typed in.
  const [otherBureau, setOtherBureau] = useState(false);

  useEffect(() => {
    if (!isStaff || !loan?.id) return undefined;
    let cancelled = false;
    axios.get(apiUrl(`/api/loans/${loan.id}/bureau-report`), { headers: headers() })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        // Nothing captured yet and the client uploaded their own report: the
        // grid starts as that report (Client-supplied, its date), not as
        // Koniza's own bureau check.
        const saved = res.data?.report;
        setForm(!saved?.id && res.data?.client_report ? fromClientReport(toForm(saved), res.data.client_report) : toForm(saved));
      })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [isStaff, loan?.id]);

  if (!isStaff || !loan?.id || !data) return null;
  const editable = mayEdit && data.can_edit;
  const report = data.report;
  const totals = bureauTotals(form.accounts);
  const age = reportAgeDays(form.report_date);
  const failed = (e, fallback) => setError((typeof e?.response?.data === 'object' && e.response.data?.error) || fallback);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setRow = (i, key, value) => setForm((f) => ({ ...f, accounts: f.accounts.map((a, k) => (k === i ? { ...a, [key]: value } : a)) }));
  const addRow = () => setForm((f) => ({ ...f, accounts: [...f.accounts, blankAccount(f.report_date, f.source)] }));
  const clientReport = data.client_report || null;
  const removeRow = (i) => setForm((f) => ({ ...f, accounts: f.accounts.filter((_, k) => k !== i) }));

  const save = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const res = await axios.put(apiUrl(`/api/loans/${loan.id}/bureau-report`), toPayload(form), { headers: headers() });
      setData((d) => ({ ...d, report: res.data?.report }));
      setForm(toForm(res.data?.report));
      setMessage('Saved. Run the calculation again so the affordability check reads it.');
      onChanged?.();
    } catch (e) {
      failed(e, 'The bureau report could not be saved.');
    }
    setBusy(false);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await axios.post(apiUrl(`/api/loans/${loan.id}/bureau-report/document`), body, { headers: headers() });
      setData((d) => ({ ...d, report: res.data?.report }));
      setMessage('The bureau report PDF is saved with the loan\'s documents.');
      onChanged?.();
    } catch (e) {
      failed(e, 'The PDF could not be uploaded.');
    }
    setBusy(false);
  };

  const input = `${fieldInputClass(false)} !text-xs`;
  return (
    <LoanSection Icon={BankIcon} title="Credit bureau report" className={className}>
      <p className="mb-3 text-xs text-gray-600 dark:text-slate-300">
        Required before a loan is granted (Microlending Act s 24(4)). Upload the report and type in every account on it:
        the affordability check counts the instalments of accounts still owed, counts microloans toward the limit of three,
        declines on a default or judgment, and declines a report more than 30 days old.
      </p>

      {/* The client's own report (ITC / TransUnion): in Documents, read by
          the calculator, and a start for the grid. Its accounts stay
          Client-supplied until they are confirmed against Koniza's own
          bureau check. */}
      {clientReport && (
        <div className="mb-3 flex flex-wrap items-center gap-2 border border-teal-200 dark:border-teal-800/60 bg-teal-50/60 dark:bg-slate-900 px-3 py-2 text-xs text-teal-900 dark:text-teal-100">
          <span>
            The client uploaded their own credit report{clientReport.report_date ? <>, dated <strong>{formatDateDMY(clientReport.report_date)}</strong></> : ''} (in Documents).
            The calculator reads its accounts, defaults and judgments by itself: nothing needs typing in for it.
            It does not replace Koniza's own bureau check: approval waits for that.
          </span>
          {editable && form.source !== 'ClientSupplied' && (
            <button type="button" onClick={() => setForm((f) => fromClientReport(f, clientReport))}
              className="ml-auto px-2 py-1 border border-teal-300 dark:border-teal-700 font-medium hover:bg-white dark:hover:bg-slate-800">
              Start from the client's report
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <label className="block">
          <span className={FIELD_LABEL}>Report date <span className="text-rose-600">*</span></span>
          <input type="date" className={input} value={form.report_date} disabled={!editable} required aria-required="true"
            onChange={(e) => setField('report_date', e.target.value)} />
          {editable && !form.report_date && (
            <span className="mt-0.5 block text-[11px] text-rose-600 dark:text-rose-400">Required: the date printed on the report.</span>
          )}
          {age !== null && (
            <span className={`mt-0.5 block text-[11px] ${age > 30 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-slate-400'}`}>
              {age < 0 ? 'In the future' : `${age} day${age === 1 ? '' : 's'} old${age > 30 ? ': too old, get a new report' : ''}`}
            </span>
          )}
        </label>
        <label className="block">
          <span className={FIELD_LABEL}>Source</span>
          <select className={input} value={form.source} disabled={!editable}
            onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, source: v, accounts: v === 'Bureau' ? f.accounts : f.accounts.map((a) => ({ ...a, source: 'ClientSupplied' })) })); }}>
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={FIELD_LABEL}>Bureau</span>
          {/* The bureaus registered in Namibia; Other takes a typed name. */}
          <select className={input} value={otherBureau ? OTHER_BUREAU : bureauChoice(form.bureau_name)} disabled={!editable} aria-label="Credit bureau"
            onChange={(e) => { const v = e.target.value; setOtherBureau(v === OTHER_BUREAU); setField('bureau_name', v === OTHER_BUREAU ? '' : v); }}>
            <option value="">Choose the bureau</option>
            {BUREAUS.map((b) => <option key={b} value={b}>{b}</option>)}
            <option value={OTHER_BUREAU}>{OTHER_BUREAU}</option>
          </select>
          {(otherBureau || bureauChoice(form.bureau_name) === OTHER_BUREAU) && (
            <input type="text" className={`${input} mt-1`} value={form.bureau_name} disabled={!editable} placeholder="Name of the bureau" aria-label="Other bureau name"
              onChange={(e) => setField('bureau_name', e.target.value)} />
          )}
        </label>
        <div>
          <span className={FIELD_LABEL}>Report (PDF)</span>
          <div className="flex items-center gap-2 min-h-[30px]">
            {report?.document_name
              ? <span className="truncate text-xs text-gray-700 dark:text-slate-200" title={report.document_name}>{report.document_name} (in Documents)</span>
              : <span className="text-xs text-gray-500 dark:text-slate-400">Not uploaded</span>}
            {editable && (
              <label className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-gray-300 dark:border-slate-600 text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer whitespace-nowrap">
                <Upload size={13} /> {report?.document_name ? 'Replace' : 'Upload'}
                <input type="file" accept="application/pdf" className="hidden" disabled={busy}
                  onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className={TABLE_HEAD_ROW}>
              {COLUMNS.map((c) => <th key={c.label || 'actions'} scope="col" className={`${cell} ${TABLE_HEAD_TEXT} ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {form.accounts.length === 0 && (
              <tr className={TABLE_ROW_LINE}>
                <td colSpan={COLUMNS.length} className={`${cell} py-2 text-gray-500 dark:text-slate-400`}>
                  {editable ? 'No accounts yet. Add each account on the report; a report with no accounts is saved as clean.' : 'No accounts captured.'}
                </td>
              </tr>
            )}
            {form.accounts.map((a, i) => (
              <tr key={i} className={TABLE_ROW_LINE}>
                <td className={`${cell} min-w-[9rem]`}>
                  <input type="text" className={input} value={a.creditor} disabled={!editable} aria-label={`Account ${i + 1} creditor`}
                    onChange={(e) => setRow(i, 'creditor', e.target.value)} />
                </td>
                <td className={`${cell} min-w-[8rem]`}>
                  <select className={input} value={a.account_type} disabled={!editable} aria-label={`Account ${i + 1} type`}
                    onChange={(e) => setRow(i, 'account_type', e.target.value)}>
                    {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className={`${cell} min-w-[6rem]`}>
                  <input type="number" min="0" step="0.01" className={`${input} text-right`} value={a.instalment} disabled={!editable} aria-label={`Account ${i + 1} instalment`}
                    onChange={(e) => setRow(i, 'instalment', e.target.value)} />
                </td>
                <td className={`${cell} min-w-[6rem]`}>
                  <input type="number" step="0.01" className={`${input} text-right`} value={a.balance} disabled={!editable} aria-label={`Account ${i + 1} balance`}
                    onChange={(e) => setRow(i, 'balance', e.target.value)} />
                </td>
                <td className={`${cell} min-w-[7rem]`}>
                  <select className={input} value={a.status} disabled={!editable} aria-label={`Account ${i + 1} status`}
                    onChange={(e) => setRow(i, 'status', e.target.value)}>
                    {ACCOUNT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className={`${cell} min-w-[8rem]`}>
                  <input type="date" className={input} value={a.report_date} disabled={!editable} aria-label={`Account ${i + 1} report date`}
                    onChange={(e) => setRow(i, 'report_date', e.target.value)} />
                </td>
                <td className={`${cell} min-w-[9rem]`}>
                  <select className={input} value={a.source || 'Bureau'} disabled={!editable} aria-label={`Account ${i + 1} source`}
                    onChange={(e) => setRow(i, 'source', e.target.value)}>
                    {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className={`${cell} text-right`}>
                  {editable && (
                    <button type="button" onClick={() => removeRow(i)} title="Remove this account" aria-label={`Remove account ${i + 1}`}
                      className="p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-slate-300">
        {editable && (
          <button type="button" onClick={addRow} className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300 hover:underline">
            <Plus size={13} /> Add account
          </button>
        )}
        <span>Instalments still owed: <strong>{toCurrency(totals.monthly)}</strong> a month ({totals.accounts} account{totals.accounts === 1 ? '' : 's'})</span>
        {totals.microloans > 0 && <span>Microloans: <strong>{totals.microloans}</strong></span>}
        {totals.adverse > 0 && <span className="text-rose-700 dark:text-rose-300">Default or judgment: <strong>{totals.adverse}</strong> (declines)</span>}
        {(form.source === 'ClientSupplied' || form.accounts.some((a) => a.source === 'ClientSupplied')) && (
          <span className="text-amber-700 dark:text-amber-300">{sourceLabel('ClientSupplied')}: not confirmed. Approval waits for Koniza's own bureau check.</span>
        )}
      </div>

      {report?.id && (
        <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">Last saved {formatDateDMY(report.updated_at)}.</p>
      )}
      {!data.can_edit && (
        <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">The report can be changed only while the loan is being decided.</p>
      )}

      {editable && (
        <div className="mt-3 flex flex-wrap items-start gap-3">
          <button type="button" onClick={save} disabled={busy || !form.report_date}
            className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60">
            {busy ? 'Saving…' : 'Save bureau report'}
          </button>
          {report?.id && <RecalculateCalculation compact loan={loan} calculator={calculator} onDone={() => onChanged?.()} />}
        </div>
      )}
      {message && <p className="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">{message}</p>}
      {error && <p className="mt-2 text-xs font-medium text-rose-700 dark:text-rose-400">{error}</p>}
    </LoanSection>
  );
};

export default BureauReportSection;
